<script setup lang="ts">
import { ArrowRight, BookOpen, ChevronDown, Compass, Menu, Moon, Search, Server, Sun, Wrench, X } from "@lucide/vue";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { useContent } from "../composables/useContent";
import { getDisplaySummary, getDisplayTitle, getPrimaryLabel, getRecordSection, normalizeSearch, SECTION_LABELS } from "../lib/content";
import { automaticTheme, millisecondsUntilThemeBoundary, savedTheme, setDocumentTheme, THEME_STORAGE_KEY, type Theme } from "../lib/theme";

const route = useRoute();
const router = useRouter();
const menuOpen = ref(false);
const mobileGroup = ref<string | null>(null);
const searchOpen = ref(false);
const searchQuery = ref("");
const searchInput = ref<HTMLInputElement | null>(null);
const headerRoot = ref<HTMLElement | null>(null);
const dark = ref(document.documentElement.dataset.theme === "dark");
const { records } = useContent();
let boundaryTimer: number | undefined;
const links = [
  { label: "知识库", to: "/library?section=knowledge", section: "knowledge", icon: BookOpen, items: [
    { label: "Linux / 系统", to: "/library?section=knowledge&topic=linux" },
    { label: "网络 / 安全", to: "/library?section=knowledge&topic=network" },
    { label: "部署 / 容器", to: "/library?section=knowledge&topic=deploy" },
    { label: "故障排查", to: "/library?section=knowledge&topic=troubleshoot" },
    { label: "开发 / AI", to: "/library?section=knowledge&topic=ai" },
  ] },
  { label: "在线工具", to: "/tools", section: "tools", icon: Wrench, items: [
    { label: "网络计算", to: "/tools/cidr" },
    { label: "数据处理", to: "/tools/json-yaml" },
    { label: "时间与自动化", to: "/tools/time" },
  ] },
  { label: "VPS", to: "/library?section=reviews", section: "reviews", icon: Server, items: [
    { label: "选择方法", to: "/entry/vpsknow-6e7ffd3e02906434f49cf122" },
    { label: "历史测评", to: "/library?section=reviews" },
    { label: "网络 / 路由", to: "/library?section=knowledge&q=路由" },
  ] },
  { label: "资源导航", to: "/library?section=resources", section: "resources", icon: Compass, items: [
    { label: "云服务", to: "/library?section=resources&category=云服务目录" },
    { label: "运维服务", to: "/library?section=resources&category=站长工具" },
    { label: "开发服务", to: "/library?section=resources&q=GitHub" },
  ] },
  { label: "关于", to: "/about", section: "about", icon: Compass, items: [] },
];
const searchScopes = links.slice(0, 4).map(({ label, to, section, icon }) => ({ label, to, section, icon }));

const activeSection = computed(() => {
  if (route.path.startsWith("/tools")) return "tools";
  if (route.path === "/about") return "about";
  if (route.name === "entry") {
    const current = records.value.find((record) => record.id === route.params.id);
    return current ? getRecordSection(current) : "";
  }
  if (route.path !== "/library") return "";
  return String(route.query.section || "knowledge");
});

const searchResults = computed(() => {
  const query = normalizeSearch(searchQuery.value);
  if (!query) return [];
  const terms = query.split(" ");
  return records.value
    .filter((record) => record.editorial_status === "published")
    .filter((record) => {
      const haystack = normalizeSearch([record.title, record.summary, ...record.categories, ...record.tags, ...record.key_facts].join(" "));
      return terms.every((term) => haystack.includes(term));
    })
    .slice(0, 5);
});

function applyTheme(theme: Theme, persist = true) {
  dark.value = theme === "dark";
  setDocumentTheme(theme);
  if (persist) {
    try { localStorage.setItem(THEME_STORAGE_KEY, theme); } catch {}
  }
}

function refreshAutomaticTheme() {
  window.clearTimeout(boundaryTimer);
  const explicit = savedTheme();
  applyTheme(explicit ?? automaticTheme(), false);
  if (!explicit) boundaryTimer = window.setTimeout(refreshAutomaticTheme, millisecondsUntilThemeBoundary());
}

function toggleSearch() {
  searchOpen.value = !searchOpen.value;
  menuOpen.value = false;
  if (searchOpen.value) void nextTick(() => searchInput.value?.focus());
}

function toggleMobileGroup(section: string) {
  mobileGroup.value = mobileGroup.value === section ? null : section;
}

function toggleMenu() {
  const opening = !menuOpen.value;
  menuOpen.value = opening;
  searchOpen.value = false;
  if (opening && links.some((item) => item.section === activeSection.value && item.items.length)) {
    mobileGroup.value = activeSection.value;
  }
}

function closeOverlays() {
  menuOpen.value = false;
  searchOpen.value = false;
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") closeOverlays();
}

function handlePointerDown(event: PointerEvent) {
  if (headerRoot.value && !headerRoot.value.contains(event.target as Node)) closeOverlays();
}

function submitSearch() {
  const value = searchQuery.value.trim();
  searchOpen.value = false;
  void router.push({ name: "library", query: value ? { q: value } : {} });
}

async function focusFirstSearchResult() {
  await nextTick();
  headerRoot.value?.querySelector<HTMLElement>(".header-search-result")?.focus();
}

function moveSearchResultFocus(event: KeyboardEvent, direction: -1 | 1) {
  const resultLinks = [...(headerRoot.value?.querySelectorAll<HTMLElement>(".header-search-result") ?? [])];
  const current = resultLinks.indexOf(event.currentTarget as HTMLElement);
  if (direction < 0 && current <= 0) {
    searchInput.value?.focus();
    return;
  }
  resultLinks[current + direction]?.focus();
}

watch(() => route.fullPath, () => {
  menuOpen.value = false;
  mobileGroup.value = null;
  searchOpen.value = false;
});
onMounted(() => {
  refreshAutomaticTheme();
  document.addEventListener("visibilitychange", refreshAutomaticTheme);
  window.addEventListener("focus", refreshAutomaticTheme);
  window.addEventListener("keydown", handleKeydown);
  document.addEventListener("pointerdown", handlePointerDown);
});
onBeforeUnmount(() => {
  window.clearTimeout(boundaryTimer);
  document.removeEventListener("visibilitychange", refreshAutomaticTheme);
  window.removeEventListener("focus", refreshAutomaticTheme);
  window.removeEventListener("keydown", handleKeydown);
  document.removeEventListener("pointerdown", handlePointerDown);
});
</script>

<template>
  <header ref="headerRoot" class="site-header">
    <div class="header-inner">
      <RouterLink class="brand" to="/" aria-label="VPScope 首页">
        <img src="/vpscope-mark.svg" alt="" width="34" height="34" />
        <span><strong>VPScope</strong><small>技术与工具</small></span>
      </RouterLink>
      <nav class="desktop-nav" aria-label="主导航">
        <div v-for="item in links" :key="item.section" class="nav-group" :class="{ 'has-menu': item.items.length }">
          <RouterLink :to="item.to" :class="{ active: activeSection === item.section }">{{ item.label }}</RouterLink>
          <div v-if="item.items.length" class="nav-submenu">
            <RouterLink v-for="child in item.items" :key="child.label" :to="child.to">{{ child.label }}</RouterLink>
          </div>
        </div>
      </nav>
      <div class="header-actions">
        <button class="icon-button" type="button" :aria-label="searchOpen ? '关闭搜索' : '搜索全站'" :title="searchOpen ? '关闭搜索' : '搜索全站'" :aria-expanded="searchOpen" aria-controls="header-search" @click="toggleSearch"><X v-if="searchOpen" :size="19" /><Search v-else :size="18" /></button>
        <button class="icon-button" type="button" :aria-label="dark ? '当前深色主题，切换到浅色主题' : '当前浅色主题，切换到深色主题'" :title="dark ? '切换到浅色主题' : '切换到深色主题'" @click="applyTheme(dark ? 'light' : 'dark')">
          <Sun v-if="dark" :size="18" /><Moon v-else :size="18" />
        </button>
        <button class="icon-button mobile-menu-button" type="button" :aria-expanded="menuOpen" aria-controls="mobile-nav" :aria-label="menuOpen ? '关闭导航' : '打开导航'" @click="toggleMenu">
          <X v-if="menuOpen" :size="20" /><Menu v-else :size="20" />
        </button>
      </div>
    </div>
    <div v-if="searchOpen" id="header-search" class="header-search-panel">
      <div class="header-search-inner">
        <form class="header-search-form" role="search" @submit.prevent="submitSearch" @keydown.esc="searchOpen = false">
          <Search :size="19" aria-hidden="true" />
          <input ref="searchInput" v-model="searchQuery" type="search" placeholder="搜索指南、命令、工具或服务" aria-label="页头搜索" autocomplete="off" @keydown.down.prevent="focusFirstSearchResult" />
          <button type="submit" aria-label="查看全部搜索结果" title="查看全部搜索结果"><Search :size="18" /></button>
        </form>
        <div v-if="searchQuery.trim()" class="header-search-results" aria-live="polite">
          <div class="header-search-summary">
            <strong>{{ searchResults.length ? `快速结果 · ${searchResults.length} 条` : "未找到匹配内容" }}</strong>
            <button v-if="searchResults.length" type="button" @click="submitSearch">查看全部</button>
          </div>
          <RouterLink v-for="result in searchResults" :key="result.id" class="header-search-result" :class="`search-result-${getRecordSection(result)}`" :to="`/entry/${result.id}`" @keydown.down.prevent="moveSearchResultFocus($event, 1)" @keydown.up.prevent="moveSearchResultFocus($event, -1)">
            <span class="search-result-type">{{ SECTION_LABELS[getRecordSection(result)] }} · {{ getPrimaryLabel(result) }}</span>
            <span class="search-result-body"><strong>{{ getDisplayTitle(result) }}</strong><small>{{ getDisplaySummary(result) }}</small></span>
            <ArrowRight :size="17" aria-hidden="true" />
          </RouterLink>
          <div v-if="!searchResults.length" class="header-search-empty">
            <p>当前公开内容中没有匹配项。</p>
            <nav class="header-search-scopes compact" aria-label="搜索范围">
              <RouterLink v-for="scope in searchScopes" :key="scope.section" class="header-search-scope" :class="`search-scope-${scope.section}`" :to="scope.to">
                <component :is="scope.icon" :size="17" aria-hidden="true" /><strong>{{ scope.label }}</strong><ArrowRight :size="16" aria-hidden="true" />
              </RouterLink>
            </nav>
          </div>
        </div>
        <div v-else class="header-search-browse">
          <div class="header-search-summary"><strong>按内容范围浏览</strong><span>{{ searchScopes.length }} 个入口</span></div>
          <nav class="header-search-scopes" aria-label="搜索范围">
            <RouterLink v-for="scope in searchScopes" :key="scope.section" class="header-search-scope" :class="`search-scope-${scope.section}`" :to="scope.to">
              <component :is="scope.icon" :size="18" aria-hidden="true" /><strong>{{ scope.label }}</strong><ArrowRight :size="16" aria-hidden="true" />
            </RouterLink>
          </nav>
        </div>
      </div>
    </div>
    <nav v-if="menuOpen" id="mobile-nav" class="mobile-nav" aria-label="移动导航">
      <div v-for="item in links" :key="item.section" class="mobile-nav-group">
        <div class="mobile-nav-primary">
          <RouterLink :to="item.to" :class="{ active: activeSection === item.section }"><component :is="item.icon" :size="18" /><span>{{ item.label }}</span></RouterLink>
          <button v-if="item.items.length" type="button" :aria-label="`${mobileGroup === item.section ? '收起' : '展开'}${item.label}`" :aria-expanded="mobileGroup === item.section" @click="toggleMobileGroup(item.section)"><ChevronDown :size="18" /></button>
        </div>
        <div v-if="mobileGroup === item.section" class="mobile-nav-children">
          <RouterLink v-for="child in item.items" :key="child.label" class="mobile-nav-child" :to="child.to">{{ child.label }}</RouterLink>
        </div>
      </div>
    </nav>
  </header>
</template>
